<template>
    <v-form ref="form" v-model="valid" data-cy="loyalty-settings">
        <v-row dense class="mb-5">
            <v-col cols="12" sm="6" md="4" order="1" order-sm="0">
                <v-text-field
                    v-model="formData.name"
                    :label="`${$t('name')}*`"
                    data-cy="name"
                    hide-details="auto"
                    :rules="[requiredRule('name')]"
                />
            </v-col>
            <v-col cols="12" sm="6" md="8" order="0">
                <v-switch
                    v-model="formData.active"
                    class="ml-auto mr-4 width-max-content"
                    :label="formData.active ? $t('active') : $t('inactive')"
                    data-cy="status"
                    hide-details
                />
            </v-col>
        </v-row>
        <TreeViewCalculation v-if="formData.pricingRule" v-model="formData.pricingRule" :is-show-vat="true" />
        <v-row class="pa-4 pt-10 gc-4">
            <v-btn :disabled="isLoading || !valid" data-cy="save" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn :disabled="isLoading" variant="text" data-cy="cancel" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import TreeViewCalculation from '~/components/salesSettings/TreeViewCalculation.vue'
    import {removeContractGroupProperties} from '~/adapters/salesSettingsAdapter'

    const props = defineProps<{
        vatId?: number
    }>()

    const emit = defineEmits<{
        (e: 'save' | 'cancel'): void
    }>()

    const initDefaultData = () => ({
        active: true,
        name: null,
        pricingRule: {
            ruleToken: 'rootRule',
            ruleValue: '',
            labelToken: 'rootRule',
            pricingRuleEntries: [],
            otherRule: false,
            ruleOperation: 'EQ',
            vatValue: 0,
            vatCode: '',
        },
    })

    const isLoading = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const formData = ref({})

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.vatId) await loadData()
        else formData.value = initDefaultData()
    })

    async function loadData(): Promise<void> {
        isLoading.value = true
        try {
            formData.value = await $api.vatPlan.get({id: props.vatId})
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        let vatData = clone(formData.value)
        vatData.pricingRule = await removeContractGroupProperties([vatData.pricingRule])[0]

        isLoading.value = true
        try {
            if (props.vatId) {
                await $api.vatPlan.put(props.vatId, vatData)
            } else {
                await $api.vatPlan.post(vatData)
            }
            $toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message || t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
